import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Col,
    Divider,
    Input,
    Row,
    Select,
    Space,
    Switch,
    Tabs,
    Typography,
} from 'antd';
import { CopyOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
// import {
//     DeductionFormConfig,
//     HAZINA_DEDUCTION_FORM_CONFIG,
// } from '@/modules/loans/components/DeductionFormConfig';

// Stub types for missing loans module
interface DeductionFormConfig {
    header?: any;
    branding?: any;
    sections?: any;
    stamp?: any;
    textContent?: any;
    company?: any;
}

const HAZINA_DEDUCTION_FORM_CONFIG: any = {
  "branding": {
    "logoUrl": "/images/hazina-logo.png",
    "watermarkUrl": "/images/hazina-logo.png",
    "showWatermark": true
  },
  "stamp": {
    "stampImageUrl": "/images/hazina_stamp.svg",
    "shape": "circle",
    "color": "#2E5CB8",
    "orgName": "HAZINA",
    "topText": "SAVINGS & CREDIT SOCIETY",
    "bottomText": "LIMITED",
    "centerLine1": "P.O.Box 9111",
    "centerLine2": "DAR ES SALAAM"
  },
  "company": {
    "name": "HAZINA SAVING AND CREDIT COOPERATIVE SOCIETY LTD",
    "address": "Madaraka Avenue, Dar Es Salaam REG. No.DSR:1822",
    "phone": "0710933944,0710606605",
    "email": "info@hazinasaccos.co.tz",
    "website": "www.hazinasaccos.co.tz",
    "poBox": "P.O.Box 9111",
    "location": "DAR ES SALAAM"
  },
  "textContent": {
    "referencePrefix": "HSCS",
    "subjectLine": "KUWEKA MAKATO YA AKIBA",
    "bodyText": "ambae anatakiwa kuwekewa makato ya AKIBA",
    "consentTitle": "RIDHAA YA MWANACHAMA",
    "consentItems": [
      "Ninakubali makato ya TZS {amount} yanatolewa kila mwezi kutoka kwenye mshahara wangu.",
      "Ninakubali makato haya yataendelea hadi nitakapokuacha kuwa mwanachama wa {companyName}.",
      "Ninakubali mabadiliko ya kiwango cha makato yangu yatatumika baada ya kuandika barua rasmi kwa ofisi.",
      "Ninathibitisha kuwa nimesoma, kuelewa na kukubali masharti yote ya kuwa mwanachama."
    ],
    "footerNote": "Fomu hii ni muhimu kwa ajili ya uthibitisho wa makato ya kila mwezi. Hakikisha imetiwa sahihi na muhuri wa mwajiri.",
    "formTitle": "LOAN DEDUCTION FORM",
    "signerTitle1": "Ushirika Daima",
    "signerTitle2": "LOAN OFFICER"
  }
};

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

interface DeductionFormConfigEditorProps {
    /** JSON string value — provided by Ant Design Form when used as a controlled field */
    value?: string;
    /** Called with the updated JSON string whenever any field changes */
    onChange?: (json: string) => void;
    /** Optional callback so parent can show a live preview */
    onPreview?: (config: DeductionFormConfig) => void;
}

// ─── helpers ────────────────────────────────────────────────────────────────

function safeParse(raw?: string): DeductionFormConfig {
    if (!raw) return {};
    try { return JSON.parse(raw); } catch { return {}; }
}

function configToFields(cfg: DeductionFormConfig) {
    const textContent = cfg.textContent || {};
    const company = cfg.company || {};
    return {
        logoUrl:        cfg.branding?.logoUrl       ?? '',
        watermarkUrl:   cfg.branding?.watermarkUrl  ?? '',
        showWatermark:  cfg.branding?.showWatermark  ?? false,
        stampImageUrl:  cfg.stamp?.stampImageUrl    ?? '',
        stampShape:     cfg.stamp?.shape            ?? 'circle',
        stampColor:     cfg.stamp?.color            ?? '#2E5CB8',
        stampOrgName:   cfg.stamp?.orgName          ?? '',
        stampTopText:   cfg.stamp?.topText          ?? '',
        stampBottomText:cfg.stamp?.bottomText       ?? '',
        stampCenter1:   cfg.stamp?.centerLine1      ?? '',
        stampCenter2:   cfg.stamp?.centerLine2      ?? '',
        companyName:    company.name                ?? '',
        companyAddress: company.address              ?? '',
        companyPhone:   company.phone               ?? '',
        companyEmail:   company.email               ?? '',
        companyWebsite: company.website             ?? '',
        companyPoBox:   company.poBox                ?? '',
        companyLocation:company.location            ?? '',
        referencePrefix:textContent.referencePrefix ?? '',
        subjectLine:    textContent.subjectLine    ?? '',
        bodyText:       textContent.bodyText       ?? '',
        consentTitle:   textContent.consentTitle   ?? '',
        consentItems:   Array.isArray(textContent.consentItems) ? textContent.consentItems : [],
        footerNote:     textContent.footerNote     ?? '',
        formTitle:      textContent.formTitle      ?? '',
        signerTitle1:   textContent.signerTitle1   ?? '',
        signerTitle2:   textContent.signerTitle2   ?? '',
    };
}

function fieldsToConfig(f: ReturnType<typeof configToFields>): DeductionFormConfig {
    return {
        branding: {
            logoUrl:      f.logoUrl      || undefined,
            watermarkUrl: f.watermarkUrl || undefined,
            showWatermark: f.showWatermark,
        },
        stamp: {
            stampImageUrl:  f.stampImageUrl  || undefined,
            shape:          f.stampShape as any,
            color:          f.stampColor     || undefined,
            orgName:        f.stampOrgName   || undefined,
            topText:        f.stampTopText   || undefined,
            bottomText:     f.stampBottomText|| undefined,
            centerLine1:    f.stampCenter1   || undefined,
            centerLine2:    f.stampCenter2   || undefined,
        },
        company: {
            name:    f.companyName    || undefined,
            address: f.companyAddress || undefined,
            phone:   f.companyPhone   || undefined,
            email:   f.companyEmail   || undefined,
            website: f.companyWebsite || undefined,
            poBox:   f.companyPoBox   || undefined,
            location:f.companyLocation|| undefined,
        },
        textContent: {
            referencePrefix: f.referencePrefix || undefined,
            subjectLine:     f.subjectLine     || undefined,
            bodyText:        f.bodyText        || undefined,
            consentTitle:    f.consentTitle    || undefined,
            consentItems:    f.consentItems && f.consentItems.length > 0 ? f.consentItems : undefined,
            footerNote:      f.footerNote      || undefined,
            formTitle:       f.formTitle       || undefined,
            signerTitle1:    f.signerTitle1    || undefined,
            signerTitle2:    f.signerTitle2    || undefined,
        },
    };
}

// ─── component ──────────────────────────────────────────────────────────────

const DeductionFormConfigEditor: React.FC<DeductionFormConfigEditorProps> = ({
    value,
    onChange,
    onPreview,
}) => {
    const [fields, setFields] = useState(configToFields(safeParse(value)));
    const [rawJson, setRawJson] = useState(value ?? '');
    const [jsonError, setJsonError] = useState<string>('');
    const [activeTab, setActiveTab] = useState<string>('form');

    // Track which direction last changed so we don't overwrite each other
    const lastSource = useRef<'form' | 'json'>('form');

    // Sync inbound value changes (e.g. when parent form loads saved data)
    useEffect(() => {
        if (value === undefined || value === null) return;
        const cfg = safeParse(value);
        setFields(configToFields(cfg));
        setRawJson(value ? JSON.stringify(cfg, null, 2) : '');
        setJsonError('');
    }, [value]);

    // ── form field change ──────────────────────────────────────────────────
    const handleField = (key: string, val: any) => {
        const next = { ...fields, [key]: val };
        setFields(next);
        const cfg = fieldsToConfig(next);
        const json = JSON.stringify(cfg);
        setRawJson(JSON.stringify(cfg, null, 2));
        setJsonError('');
        lastSource.current = 'form';
        onChange?.(json);
    };

    // ── raw JSON change ────────────────────────────────────────────────────
    const handleRawJson = (txt: string) => {
        setRawJson(txt);
        lastSource.current = 'json';
        try {
            const cfg = JSON.parse(txt) as DeductionFormConfig;
            setFields(configToFields(cfg));
            setJsonError('');
            onChange?.(txt);
        } catch (e: any) {
            setJsonError(e.message);
        }
    };

    // ── load Hazina defaults ───────────────────────────────────────────────
    const handleLoadHazina = () => {
        const cfg = HAZINA_DEDUCTION_FORM_CONFIG;
        setFields(configToFields(cfg));
        const json = JSON.stringify(cfg);
        setRawJson(JSON.stringify(cfg, null, 2));
        setJsonError('');
        onChange?.(json);
    };

    const handlePreview = () => {
        const cfg = fieldsToConfig(fields);
        onPreview?.(cfg);
    };

    const handleCopyJson = () => {
        navigator.clipboard.writeText(rawJson).catch(() => {});
    };

    // ── render ─────────────────────────────────────────────────────────────
    const f = fields;

    return (
        <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
            <Space style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                <Button size="small" icon={<ReloadOutlined />} onClick={handleLoadHazina}>
                    Load Hazina Defaults
                </Button>
                {onPreview && (
                    <Button size="small" icon={<EyeOutlined />} onClick={handlePreview}>
                        Preview Form
                    </Button>
                )}
            </Space>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="small"
                items={[
                    {
                        key: 'form',
                        label: 'Structured Editor',
                        children: (
                            <div>
                                <Divider orientation="left" style={{ margin: '8px 0' }}>Branding &amp; Logo</Divider>
                                <Row gutter={12}>
                                    <Col xs={24} md={16}>
                                        <div style={{ marginBottom: 8 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Logo URL / Path</Text>
                                            <Input
                                                value={f.logoUrl}
                                                onChange={e => handleField('logoUrl', e.target.value)}
                                                placeholder="/images/hazina-logo.png"
                                                size="small"
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <div style={{ marginBottom: 8 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Show Watermark</Text>
                                            <div>
                                                <Switch
                                                    checked={f.showWatermark}
                                                    onChange={v => handleField('showWatermark', v)}
                                                    checkedChildren="Yes"
                                                    unCheckedChildren="No"
                                                    size="small"
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                {f.showWatermark && (
                                    <div style={{ marginBottom: 8 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Watermark Image URL / Path</Text>
                                        <Input
                                            value={f.watermarkUrl}
                                            onChange={e => handleField('watermarkUrl', e.target.value)}
                                            placeholder="Leave empty to use logo URL"
                                            size="small"
                                        />
                                    </div>
                                )}

                                <Divider orientation="left" style={{ margin: '8px 0' }}>Stamp</Divider>
                                <div style={{ marginBottom: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Stamp Image URL (overrides SVG stamp)</Text>
                                    <Input
                                        value={f.stampImageUrl}
                                        onChange={e => handleField('stampImageUrl', e.target.value)}
                                        placeholder="/images/hazina_stamp.svg (leave empty to draw SVG)"
                                        size="small"
                                    />
                                </div>
                                <Row gutter={12}>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Shape</Text>
                                        <Select
                                            value={f.stampShape}
                                            onChange={v => handleField('stampShape', v)}
                                            size="small"
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="circle">Circle</Option>
                                            <Option value="square">Square</Option>
                                            <Option value="rectangle">Rectangle</Option>
                                        </Select>
                                    </Col>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Colour</Text>
                                        <Input
                                            value={f.stampColor}
                                            onChange={e => handleField('stampColor', e.target.value)}
                                            placeholder="#2E5CB8"
                                            size="small"
                                        />
                                    </Col>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Org Short Name</Text>
                                        <Input
                                            value={f.stampOrgName}
                                            onChange={e => handleField('stampOrgName', e.target.value)}
                                            placeholder="HAZINA"
                                            size="small"
                                        />
                                    </Col>
                                </Row>
                                <Row gutter={12} style={{ marginTop: 8 }}>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Top Text</Text>
                                        <Input
                                            value={f.stampTopText}
                                            onChange={e => handleField('stampTopText', e.target.value)}
                                            placeholder="SAVINGS & CREDIT SOCIETY"
                                            size="small"
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Bottom Text</Text>
                                        <Input
                                            value={f.stampBottomText}
                                            onChange={e => handleField('stampBottomText', e.target.value)}
                                            placeholder="LIMITED"
                                            size="small"
                                        />
                                    </Col>
                                </Row>
                                <Row gutter={12} style={{ marginTop: 8 }}>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Centre Line 1</Text>
                                        <Input
                                            value={f.stampCenter1}
                                            onChange={e => handleField('stampCenter1', e.target.value)}
                                            placeholder="P.O.Box 9111"
                                            size="small"
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Centre Line 2</Text>
                                        <Input
                                            value={f.stampCenter2}
                                            onChange={e => handleField('stampCenter2', e.target.value)}
                                            placeholder="DAR ES SALAAM"
                                            size="small"
                                        />
                                    </Col>
                                </Row>

                                <Divider orientation="left" style={{ margin: '8px 0' }}>Company Information</Divider>
                                <div style={{ marginBottom: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Company Name</Text>
                                    <Input
                                        value={f.companyName}
                                        onChange={e => handleField('companyName', e.target.value)}
                                        placeholder="HAZINA SAVING AND CREDIT COOPERATIVE SOCIETY LTD"
                                        size="small"
                                    />
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Address</Text>
                                    <Input
                                        value={f.companyAddress}
                                        onChange={e => handleField('companyAddress', e.target.value)}
                                        placeholder="Madaraka Avenue, Dar Es Salaam REG. No.DSR:1822"
                                        size="small"
                                    />
                                </div>
                                <Row gutter={12}>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Phone</Text>
                                        <Input
                                            value={f.companyPhone}
                                            onChange={e => handleField('companyPhone', e.target.value)}
                                            placeholder="0710933944,0710606605"
                                            size="small"
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Email</Text>
                                        <Input
                                            value={f.companyEmail}
                                            onChange={e => handleField('companyEmail', e.target.value)}
                                            placeholder="info@hazinasaccos.co.tz"
                                            size="small"
                                        />
                                    </Col>
                                </Row>
                                <Row gutter={12} style={{ marginTop: 8 }}>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Website</Text>
                                        <Input
                                            value={f.companyWebsite}
                                            onChange={e => handleField('companyWebsite', e.target.value)}
                                            placeholder="www.hazinasaccos.co.tz"
                                            size="small"
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>P.O. Box</Text>
                                        <Input
                                            value={f.companyPoBox}
                                            onChange={e => handleField('companyPoBox', e.target.value)}
                                            placeholder="P.O.Box 9111"
                                            size="small"
                                        />
                                    </Col>
                                </Row>
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Location</Text>
                                    <Input
                                        value={f.companyLocation}
                                        onChange={e => handleField('companyLocation', e.target.value)}
                                        placeholder="DAR ES SALAAM"
                                        size="small"
                                    />
                                </div>

                                <Divider orientation="left" style={{ margin: '8px 0' }}>Form Text</Divider>
                                <Row gutter={12}>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Reference Prefix</Text>
                                        <Input value={f.referencePrefix} onChange={e => handleField('referencePrefix', e.target.value)} placeholder="HSCS" size="small" />
                                    </Col>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Form Title</Text>
                                        <Input value={f.formTitle} onChange={e => handleField('formTitle', e.target.value)} placeholder="LOAN DEDUCTION FORM" size="small" />
                                    </Col>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Consent Title</Text>
                                        <Input value={f.consentTitle} onChange={e => handleField('consentTitle', e.target.value)} placeholder="RIDHAA YA MWANACHAMA" size="small" />
                                    </Col>
                                </Row>
                                <Row gutter={12} style={{ marginTop: 8 }}>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Subject Line (YAH:)</Text>
                                        <Input value={f.subjectLine} onChange={e => handleField('subjectLine', e.target.value)} placeholder="KUWEKA MAKATO YA MWANACHAMA" size="small" />
                                    </Col>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Body Text Descriptor</Text>
                                        <Input value={f.bodyText} onChange={e => handleField('bodyText', e.target.value)} placeholder="ambae analakiwa kuwekewa makato ya AKIBA" size="small" />
                                    </Col>
                                </Row>
                                <Row gutter={12} style={{ marginTop: 8 }}>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Signer Title 1</Text>
                                        <Input value={f.signerTitle1} onChange={e => handleField('signerTitle1', e.target.value)} placeholder="Ushirika Daima" size="small" />
                                    </Col>
                                    <Col xs={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Signer Title 2 (Role)</Text>
                                        <Input value={f.signerTitle2} onChange={e => handleField('signerTitle2', e.target.value)} placeholder="DISBURSEMENT OFFICER" size="small" />
                                    </Col>
                                </Row>
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Footer Note</Text>
                                    <Input value={f.footerNote} onChange={e => handleField('footerNote', e.target.value)} placeholder="Fomu hii ni muhimu..." size="small" />
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Consent Items (one per line, use {'{amount}'} and {'{companyName}'} as placeholders)</Text>
                                    <TextArea
                                        value={Array.isArray(f.consentItems) ? f.consentItems.join('\n') : ''}
                                        onChange={e => {
                                            const lines = e.target.value.split('\n').filter(line => line.trim());
                                            handleField('consentItems', lines);
                                        }}
                                        rows={4}
                                        size="small"
                                        placeholder="Ninakubali makato ya TZS {amount} yanatolewa kila mwezi..."
                                        style={{ fontFamily: 'monospace', fontSize: 11 }}
                                    />
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: 'json',
                        label: 'JSON Config',
                        children: (
                            <div>
                                <Alert
                                    message="Paste or edit the raw JSON configuration. Changes here are immediately reflected in the structured editor."
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 8 }}
                                />
                                {jsonError && (
                                    <Alert
                                        message={`JSON error: ${jsonError}`}
                                        type="error"
                                        showIcon
                                        style={{ marginBottom: 8 }}
                                    />
                                )}
                                <TextArea
                                    value={rawJson}
                                    onChange={e => handleRawJson(e.target.value)}
                                    rows={18}
                                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                                    placeholder='{"stamp":{"orgName":"HAZINA",...},"branding":{"logoUrl":"/images/hazina-logo.png"}}'
                                />
                                <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={handleCopyJson}
                                    style={{ marginTop: 8 }}
                                >
                                    Copy JSON
                                </Button>
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
};

export default DeductionFormConfigEditor;
